<script>
    import {tick} from "svelte";
    import CompanyChooser from "../CompanyChooser.svelte";
    import ViewLogs from "../ViewLogs.svelte";
    import ViewNotes from "../ViewNotes.svelte";
    import {PAGE_ID, router, navigateTo} from "../../stores/router";
    import {app_state, displayPersonFromID, selectRequest, selectPerson, updatePerson, filemanager} from "../../stores/misc";
    import {I_PERSON, getNullPerson, personAsObject, I_COMPANY, I_REQUEST, PERSON_LABELS} from "../../../../../../lib/datatypes";
    import {deepCopyByValue, sortCompareASC, prettyHTMLFromPlainText} from "../../../../../../lib/utils";
    import {companies} from "../../stores/companies";
    import RequestsList from "../RequestsList.svelte";
    import {requests} from "../../stores/requests";
    import DisplayTags from "../DisplayTags.svelte";
    import TagAdder from "../TagAdder.svelte";
    import FileAttachmentList from "../FileAttachmentList.svelte";

    // 'Empty' display person for use when there is no selected person
    let empty_person_obj = personAsObject(getNullPerson());
    empty_person_obj.positions = [];

    $: selected_person = $app_state.selected_person_id === null ? empty_person_obj : displayPersonFromID($app_state.selected_person_id);
    let edited_selected_person = null;
    let notes_selected_person = [];
    let logs_selected_person = [];
    let selected_person_new_note = '';
    const resetStateParams = () => {
        edited_selected_person = null;
        notes_selected_person = [];
        logs_selected_person = [];
        selected_person_new_note = '';
    };
    $: (async () => {
        if($app_state.selected_person_id === null) {
            resetStateParams();
        } else {
            const response = await fetch(`/api/person/${$app_state.selected_person_id}`);
            const result = await response.json();
            notes_selected_person = result.notes;
            logs_selected_person = result.log;
        }
    })()

    $: person_request_arr_list = $requests.list.filter(request_arr => request_arr[I_REQUEST.ID_PERSON] == selected_person.id);

    const VIEW_STATE = {
        VIEWING: 0,
        EDITING: 1
    };
    let view_state = VIEW_STATE.VIEWING;

    const TAB_STATE = {
        REQUESTS: 0,
        NOTES: 1,
        LOG: 2
    };
    let tab_state = TAB_STATE.NOTES;

    const onEditPersonClick = async () => {
        edited_selected_person = await deepCopyByValue(selected_person);
        // console.log(edited_selected_person);
        view_state = VIEW_STATE.EDITING;
    };

    let position_being_edited = null;
    const onEditCompanyClick = index => {
        position_being_edited = index;
    };
    const onCompanyClick = company_id => {
        if(position_being_edited !== null) {
            edited_selected_person.positions[position_being_edited].id_company = company_id;
            edited_selected_person.positions[position_being_edited].company_name = $companies.lookup[company_id][I_COMPANY.NAME];
            position_being_edited = null;
        }
        else {
            console.warn(`no position being edited, why was this company clicked?`);
        }
    };
    const resetEditingPerson = () => {
        view_state = VIEW_STATE.VIEWING;
        edited_selected_person = null;
    }
    const onDeleteTagClicked = tag_id => {
        edited_selected_person.tags = edited_selected_person.tags.split(',')
            .map(id => id.trim())
            .filter(id => id !== tag_id)
            .sort(sortCompareASC)        // sorting to keep things tidy
            .join(',');
    };
    const onAddTagClicked = tag_id => {
        edited_selected_person.tags = edited_selected_person.tags.split(',')
            .map(id => id.trim())
            .filter(id => id !== tag_id) // do a filter step to remove it if it's already in the list - so no duplicates!
            .concat(tag_id)              // concat rather than push, as concat returns an array, push the item added
            .sort(sortCompareASC)        // sorting to keep things tidy
            .join(',');
    };
    const saveEditedPerson = async () => {
        if(edited_selected_person === null) console.warn('Trying to save a null edited person!');
        // TODO write a handling function in misc to handle saving of a display person object (equiv to addPerson)
        let saved_person = await updatePerson(edited_selected_person);
        await tick(); // ..have to wait for reactive structures to update

        selectPerson(saved_person[I_PERSON.ID]); // ..to reflect the changes made (otherwise doesn't pick it up)
        resetEditingPerson();
    };


    let is_uploading_files = false;
    const doChooseFile = () => {
        filemanager.chooseFiles();
        is_uploading_files = true;
    };
    $: {
        if (is_uploading_files) {
            if ($filemanager.files.length > 0) {
                if (edited_selected_person !== null) {
                    edited_selected_person.files = edited_selected_person.files.concat($filemanager.files);
                    edited_selected_person = edited_selected_person;
                    is_uploading_files = false;
                    filemanager.resetFiles();
                    console.log('files added, full list =', edited_selected_person.files);
                }
            }
        }
    }


    const onBackClick = () => {
        if($router.context.previous_page_id !== null) {
            navigateTo($router.context.previous_page_id);
        }
        else {
            navigateTo(PAGE_ID.PEOPLE);
        }
        resetEditingPerson();
    };

    const onRequestClick = id => {
        selectRequest(id);
        navigateTo(PAGE_ID.VIEW_REQUEST, PAGE_ID.VIEW_PERSON);
    };
</script>


<div class="absolute top-16 right-0 bottom-0 left-0 overflow-y-auto" class:hidden={$router.current_page !== PAGE_ID.VIEW_PERSON}>

    <!-- Header -->
    <div class="px-4 sm:px-6 lg:px-8">

        <!--<Breadcrumbs></Breadcrumbs>-->

        <div class="h-16 flex items-center justify-between">
            <a on:click|preventDefault={onBackClick} href="/" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
                <svg class="-ml-1 mr-1 h-5 w-5 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
                </svg>
                Back
            </a>
            <div class="flex flex-shrink-0 md:mt-0 md:ml-4">
                <button on:click={onEditPersonClick}
                        type="button" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Edit</button>
                <!--<button type="button" class="ml-3 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Publish</button>-->
            </div>
        </div>

    </div>

    <main class="pb-10">

        {#if view_state === VIEW_STATE.VIEWING}
        <!-- Page header -->
        <div class="mx-auto max-w-3xl px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">

            <!-- Person pill-->
            <div class="flex inline-flex items-center px-2 py-1 rounded-full ring-1 ring-inset ring-gray-300">
                <svg class="h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z" />
                </svg>
                <p class="ml-1 text-sm uppercase font-light text-gray-600">Person</p>
            </div>

            <div class="mt-4">
                <h1 class="text-2xl font-bold text-gray-900">{selected_person.first_name} {selected_person.last_name}</h1>
                <p class="text-sm font-medium text-gray-500">{selected_person.positions.length === 0 ? '' : selected_person.positions[0].position}</p>
                <p class="text-sm font-medium text-gray-900">{selected_person.positions.length === 0 ? '' : selected_person.positions[0].company_name}</p>
            </div>

        </div>

        <div class="px-4 sm:px-6 lg:px-8">
<!--                <p class="text-sm font-medium text-gray-500">Email address</p>-->
            <p class="mt-2 text-sm text-gray-900">
                {#each selected_person.emails.split(',') as email}
                    {email}
                {/each}
            </p>
<!--                <p class="text-sm font-medium text-gray-500">Phone</p>-->
            <p class="mt-2 text-sm text-gray-900">
                {#each selected_person.phone_numbers.split(',') as phone}
                    {phone}
                {/each}
            </p>
<!--                <p class="text-sm font-medium text-gray-500">Tags</p>-->
            <p class="mt-4 text-sm text-gray-900">
                <DisplayTags tag_id_list={selected_person.tags}></DisplayTags>
            </p>

            <p class="mt-4 text-gray-900">
                {@html prettyHTMLFromPlainText(selected_person.description)}
            </p>

            {#if selected_person.files.length > 0}
                <div class="my-4">
                    <p class="mt-4 text-sm text-gray-500">Files:</p>
                    <FileAttachmentList files={selected_person.files}></FileAttachmentList>
                </div>
            {/if}

        </div>
        {/if}
        {#if view_state === VIEW_STATE.EDITING}
        <div class="px-4 sm:px-6 lg:px-8">
            <div class="mt-4">
                <label for="first-name" class="block text-sm font-medium text-gray-900">First name</label>
                <input bind:value="{edited_selected_person.first_name}" type="text" name="first-name" id="first-name" autocomplete="given-name" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>

            <div class="mt-4">
                <label for="last-name" class="block text-sm font-medium text-gray-900">Last name</label>
                <input bind:value="{edited_selected_person.last_name}" type="text" name="last-name" id="last-name" autocomplete="family-name" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>

            {#each edited_selected_person.positions as position, i}
                <div class="mt-4">
                    <label for="job-title" class="block text-sm font-medium text-gray-900">Position</label>
                    <input bind:value="{position.position}" type="text" name="job-title" id="job-title" autocomplete="organization-title" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
                </div>

                <div class="mt-4">
                    <label for="company" class="block text-sm font-medium text-gray-900">Company</label>
                    {#if i === position_being_edited}
                        <CompanyChooser
                                type="box"
                                selected_company_id={position.id_company}
                                current_search_str={position.company_name}
                                on:companyClicked={(e) => onCompanyClick(e.detail.id)}
                                on:cancelClicked={() => position_being_edited = null}></CompanyChooser>
                    {:else}
                        <input on:focus={() => onEditCompanyClick(i)} value="{position.company_name}" type="text" name="company" id="company" autocomplete="organization" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
                    {/if}
                </div>
            {/each}

            <div class="mt-4">
                <label for="email" class="block text-sm font-medium text-gray-900">Email Addresses</label>
                <input bind:value="{edited_selected_person.emails}" type="email" name="email" id="email" multiple autocomplete="email" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>

            <div class="mt-4">
                <label for="phone-number" class="block text-sm font-medium text-gray-900">Phone Number</label>
                <input bind:value="{edited_selected_person.phone_numbers}" type="tel" name="phone-number" id="phone-number" multiple autocomplete="tel" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>

            <div class="mt-4">
                <label for="tags" class="block text-sm font-medium text-gray-900">Tags</label>
                <div class="mt-1">
                    <DisplayTags show_delete_buttons={true} tag_id_list={edited_selected_person.tags} on:deleteClicked={(e) => onDeleteTagClicked(e.detail.id)}></DisplayTags>
                </div>
                <TagAdder tag_id_list={edited_selected_person.tags} on:tagClicked={(e) => onAddTagClicked(e.detail.id)}></TagAdder>
                <p class="mt-3 text-sm italic text-gray-500">({@html PERSON_LABELS.TAGS})</p>
            </div>

            <div class="mt-4">
                <label for="description" class="block text-sm font-medium text-gray-900">Description</label>
                <div class="mt-1">
                    <textarea bind:value={edited_selected_person.description}
                              id="description" name="description" rows="4"
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"></textarea>
                </div>
                <p class="mt-3 text-sm italic text-gray-500">({@html PERSON_LABELS.DESCRIPTION})</p>
            </div>

            <div class="my-4">
                <p class="block text-sm font-medium text-gray-900">Files</p>
                {#if edited_selected_person.files.length > 0}
                    <div class="mt-2">
                        <FileAttachmentList files={edited_selected_person.files}></FileAttachmentList>
                    </div>
                {/if}
                {#if $filemanager.is_uploading}
                    <div class="flex items-center justify-center rounded-md border border-transparent bg-orange-600 px-2 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    ><svg class="animate-spin self-center w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                        Sending..</div>
                {/if}
                <button on:click={doChooseFile}
                        type="button"
                        class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                >Add File</button>
            </div>

            <div class="flex justify-end pt-8">
                <button on:click={resetEditingPerson}
                        type="button"
                        class="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >Cancel</button>
                <button on:click={saveEditedPerson}
                        type="submit"
                        class="ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >Save</button>
            </div>

        </div>

        {/if}

        <div class="block mt-6">
            <div class="border-b border-gray-200">
                <nav class="-mb-px px-4 flex justify-between sm:justify-start space-x-8" aria-label="Tabs">
                    <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.REQUESTS} class="{tab_state === TAB_STATE.REQUESTS ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium">
                        <svg class="{tab_state === TAB_STATE.REQUESTS ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5"  fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                        </svg>
                        <span>Requests</span>
                    </a>

                    <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.NOTES} class="{tab_state === TAB_STATE.NOTES ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium">
                        <svg class="{tab_state === TAB_STATE.NOTES ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5"  fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                        </svg>
                        <span>Notes</span>
                    </a>

                    <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.LOG} class="{tab_state === TAB_STATE.LOG ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium" aria-current="page">
                        <svg class="{tab_state === TAB_STATE.LOG ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        <span>Log</span>
                    </a>
                </nav>
            </div>
        </div>

        {#if tab_state === TAB_STATE.REQUESTS}
            <div class="px-4 sm:px-6 lg:px-8">
                <RequestsList requests_arr_list={person_request_arr_list} on:requestClicked={(e) => onRequestClick(e.detail.id)}></RequestsList>
            </div>
        {/if}

        {#if tab_state === TAB_STATE.NOTES}
        <ViewNotes type="person" owner_id={selected_person.id} bind:notes={notes_selected_person}></ViewNotes>
        {/if}

        {#if tab_state === TAB_STATE.LOG}
        <ViewLogs type="person" owner_id={selected_person.id} bind:logs={logs_selected_person}></ViewLogs>
        {/if}

    </main>

</div>


<style></style>